import { SubSink } from 'subsink';
import { PlatformService } from './../../../services/platform/platform.service';
import { ModalController, ToastController } from '@ionic/angular';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { PrimaryInformationService } from 'src/app/services/primary-information/primary-information.service';
import { Router } from '@angular/router';
import { IsUserLoggedInService } from 'src/app/services/user/is-user-logged-in.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-primary-info',
  templateUrl: './primary-info.page.html',
  styleUrls: ['./primary-info.page.scss'],
})
export class PrimaryInfoPage implements OnInit {
  isMobileDevice: boolean;
  primaryInfoForm: FormGroup;
  subscription = new SubSink();
  showSpinner: boolean = false;

  response_message: string = '';
  hasResponseError: boolean = false;

  storedFirstName: string = '';
  storedLastName: string = '';

  constructor(
    private fb: FormBuilder,
    private platform: PlatformService,
    private modalCtrl: ModalController,
    private toast: ToastController,
    public router: Router,
    private primaryInfoService: PrimaryInformationService,
    private loggedInService: IsUserLoggedInService,
    private auth: AuthService
  ) {
    this.isMobileDevice = this.platform.isMobile();
  }

  ngOnInit() {
    this.readyPrimaryInfoForm();
    this.getPrimaryInfoData();
  }

  ionViewWillLeave(){
    this.subscription.unsubscribe();
  }

  readyPrimaryInfoForm() {
    this.primaryInfoForm = this.fb.group({
      first_name: ['', [Validators.required, Validators.pattern(/\S+/) ]],
      last_name: ['', [Validators.required, Validators.pattern(/\S+/) ]],
      user_name: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9_]+$')]],
      email: ['', [Validators.required, Validators.pattern('^[^@]+@([-\\w]+\\.)+[A-Za-z]{2,4}$')]],
      title: ['', Validators.pattern(/\S+/)],
      tag_line: ['', Validators.pattern(/\S+/)],
      bio: ['', Validators.pattern(/\S+/)],
      website:  ['', [Validators.pattern('^https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)')]],
    });
  }

  readyFormData(res: any) {
    this.storedFirstName = res.first_name;
    this.storedLastName = res.last_name;
    this.primaryInfoForm.patchValue({
      first_name: res.first_name ?? '',
      last_name: res.last_name ?? '',
      user_name: res.user_name ?? '',
      email: res.email ?? '',
      title: res.title ?? '',
      tag_line: res.tag_line ?? '',
      bio: res.bio ?? '',
      website: res.website ?? '',
    });
  }

  get first_name() {
    return this.primaryInfoForm.get('first_name');
  }

  get last_name() {
    return this.primaryInfoForm.get('last_name');
  }

  get user_name() {
    return this.primaryInfoForm.get('user_name');
  }

  get email() {
    return this.primaryInfoForm.get('email');
  }

  get title() {
    return this.primaryInfoForm.get('title');
  }

  get tag_line() {
    return this.primaryInfoForm.get('tag_line');
  }

  get bio() {
    return this.primaryInfoForm.get('bio');
  }

  get website() {
    return this.primaryInfoForm.get('website');
  }

  async getPrimaryInfoData() {
    this.subscription.sink = await this.primaryInfoService
      .getPrimaryInfo()
      .subscribe(
        (res_primaryInfo: any) => {
          this.readyFormData(res_primaryInfo.data);
        },
        (error) => {
          console.log(error);
        }
      );
  }

  async updatePrimaryInfo() {
    this.showSpinner = true;
    let primaryInfo = this.primaryInfoForm.value;

    let update_primary_info = {
      user_name: primaryInfo.user_name,
      title: primaryInfo.title,
      tag_line: primaryInfo.tag_line,
      bio: primaryInfo.bio,
      website: primaryInfo.website,
    }
    if (this.first_name.dirty || this.last_name.dirty) {
      update_primary_info['first_name'] = primaryInfo.first_name;
      update_primary_info['last_name'] = primaryInfo.last_name;
    }
    if (this.email.dirty) {
      update_primary_info['email'] = primaryInfo.email;
    }

    this.subscription.sink = await this.primaryInfoService
      .updatePrimaryInfo(update_primary_info)
      .subscribe(
        (res: any) => {
          this.showSpinner = false;
          // console.log('update primary response ----------> ', res);
          this.hasResponseError = false;
          this.response_message = res.message;
          this.presentSuccessToast(this.response_message);
          if (this.email.dirty) {
            let message = 'Your email has been updated. Please sign in again using your new email.';
            setTimeout(() => {
              this.logOut();
              this.presentSuccessToast(message);
            }, 2000);
          }
          let newFirstName = primaryInfo.first_name;
          let newLastName = primaryInfo.last_name;
          if (this.storedFirstName !== newFirstName || this.storedLastName !== newLastName) {
            this.updateProfileStorage(newFirstName, newLastName);
          }
        },
        (err: any) => {
          this.showSpinner = false;
          // console.log('Error -------> ', err);
          if (err.status == 400) {
            this.response_message = err.error.message;
          } else {
            this.response_message = "An error occurred updating your Primary information, please try again later.";
          }
          this.hasResponseError = true;
          this.presentErrorToast(this.response_message);
        }
      );
  }

  updateProfileStorage(newFirstName, newLastName) {
    let userProfile = JSON.parse(localStorage.getItem('user_profile'));
    userProfile.first_name = newFirstName;
    userProfile.last_name = newLastName;
    localStorage.setItem('user_profile', JSON.stringify(userProfile));

    let userData = JSON.parse(localStorage.getItem('user_data'));
    userData.user.displayName = newFirstName + ' ' + newLastName;
    localStorage.setItem('user_data', JSON.stringify(userData));
  }

  async presentSuccessToast(res_message) {
    const toast = await this.toast.create({
      header: 'Success',
      color: 'success',
      message: res_message,
      position: 'top',
      duration: 3000,
    });
    await toast.present();
  }

  async presentErrorToast(res_message) {
    const toast = await this.toast.create({
      header: 'Failed',
      color: 'danger',
      message: res_message,
      position: 'top',
      duration: 3000,
    });
    await toast.present();
  }

  closeModal() {
    return this.modalCtrl.dismiss();
  }

  async logOut(){
    this.loggedInService.changeUserStatus(0);
    await this.auth.logout();
    this.router.navigate(['login']);
  }
}
